import {Component, OnInit, computed, signal} from '@angular/core';
import {FinancialService} from '../services/financial.service';
import {Utils} from '../utils.class';
import {AfasService} from '../services/afas.service';
import {combineLatest} from 'rxjs';
import {AfasInvoice} from "../classes/invoice";
import {User} from "../classes/user.class";
import {UserService} from "../services/user/user.service";
import {CodaltComponent} from '../codalt.component';
import {CurrencyPipe, DatePipe, DecimalPipe} from '@angular/common';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {FilterPipe} from '../pipes/filter.pipe';
import {SumPipe} from '../pipes/sum.pipe';
import {Company} from '../classes/company';
import {RouterLink} from '@angular/router';
import {HasFunctionalityPipe} from '../pipes/has-functionality.pipe';
import {CompanyService} from '../services/company.service';
import {RegistrationService, RegistrationTotal} from '../services/registration.service';

interface HoursSplit {
    internal: number;
    groundbirds: number;
    external: number;
}

@Component({
    selector: 'app-financial',
    templateUrl: './financial.component.html',
    styleUrls: ['./financial.component.scss'],
    imports: [
        ReactiveFormsModule,
        FormsModule,
        CurrencyPipe,
        DatePipe,
        DecimalPipe,
        FilterPipe,
        SumPipe,
        RouterLink,
        HasFunctionalityPipe
    ]
})
export class FinancialComponent extends CodaltComponent implements OnInit {

    readonly hourlyCompanies = signal<Company[]>(null);
    readonly hourlyEmployees = signal<User[]>(null);
    readonly invoices = signal<AfasInvoice[]>(null);
    readonly visibleInvoices = computed(() => {
        const invoices = this.invoices();
        if (!invoices) return [];
        const nineWeeksAgo = new Date();
        nineWeeksAgo.setDate(nineWeeksAgo.getDate() - 9 * 7);
        return invoices.filter(invoice => new Date(invoice.nookPieceDate) >= nineWeeksAgo || invoice.betaald !== invoice.amtInvoice);
    });
    readonly externalDebtorIds: string[] = ['10426', '10432', '10430', '10425', '10431', '10427', '10427', '10428', '10439', '10441'];
    readonly currentYearInvoices = computed(() => {
        const invoices = this.invoices();
        if (!invoices) return [];
        const currentYear = new Date().getFullYear();
        return invoices.filter(invoice => new Date(invoice.nookPieceDate).getFullYear() === currentYear);
    });
    readonly previousYearInvoices = computed(() => {
        const invoices = this.invoices();
        if (!invoices) return [];
        const previousYear = new Date().getFullYear() - 1;
        return invoices.filter(invoice => new Date(invoice.nookPieceDate).getFullYear() === previousYear);
    });
    readonly currentYearRevenueByDebtor = computed(() => FinancialComponent.totalsByDebtor(this.currentYearInvoices()));
    readonly previousYearRevenueByDebtor = computed(() => FinancialComponent.totalsByDebtor(this.previousYearInvoices()));
    readonly currentYearLabel = new Date().getFullYear();
    readonly previousYearLabel = new Date().getFullYear() - 1;
    readonly currentYearExternalTotal = computed(() => Utils.sumProps(
        this.currentYearInvoices().filter(invoice => this.externalDebtorIds.includes(invoice.debtorId)), 'totaalExclBtw'
    ));
    readonly currentYearInternalTotal = computed(() => Utils.sumProps(
        this.currentYearInvoices().filter(invoice => !this.externalDebtorIds.includes(invoice.debtorId)), 'totaalExclBtw'
    ));
    readonly previousYearExternalTotal = computed(() => Utils.sumProps(
        this.previousYearInvoices().filter(invoice => this.externalDebtorIds.includes(invoice.debtorId)), 'totaalExclBtw'
    ));
    readonly previousYearInternalTotal = computed(() => Utils.sumProps(
        this.previousYearInvoices().filter(invoice => !this.externalDebtorIds.includes(invoice.debtorId)), 'totaalExclBtw'
    ));
    readonly previousYearTotal = computed(() => this.previousYearInternalTotal() + this.previousYearExternalTotal());
    readonly previousYearInternalPercentage = computed(() => this.previousYearTotal() ? (this.previousYearInternalTotal() / this.previousYearTotal()) * 100 : 0);
    readonly previousYearExternalPercentage = computed(() => this.previousYearTotal() ? (this.previousYearExternalTotal() / this.previousYearTotal()) * 100 : 0);
    readonly currentYearTotal = computed(() => this.currentYearInternalTotal() + this.currentYearExternalTotal());
    readonly currentYearInternalPercentage = computed(() => this.currentYearTotal() ? (this.currentYearInternalTotal() / this.currentYearTotal()) * 100 : 0);
    readonly currentYearExternalPercentage = computed(() => this.currentYearTotal() ? (this.currentYearExternalTotal() / this.currentYearTotal()) * 100 : 0);
    readonly currentYearTotals = signal<RegistrationTotal[]>([]);
    readonly previousYearTotals = signal<RegistrationTotal[]>([]);
    readonly allCompanies = signal<Company[]>([]);
    readonly currentYearHoursByUser = computed(() => FinancialComponent.hoursByUser(this.currentYearTotals(), this.allCompanies()));
    readonly previousYearHoursByUser = computed(() => FinancialComponent.hoursByUser(this.previousYearTotals(), this.allCompanies()));
    showSubs = new FormControl<boolean>(false);
    readonly users = signal<User[]>(null);
    hourlyRate = 100;

    utils = Utils;
    readonly hasInvoice = signal<null | number[]>([]);
    readonly visibleInvoiceDates = computed(() => Array.from(new Set(this.visibleInvoices().map(invoice => invoice.nookPieceDate))));
    invoicing: number;

    currentYear: number;
    currentPeriod: number;
    previousPeriod: number;

    constructor(
        private afasService: AfasService,
        private companyService: CompanyService,
        private financialService: FinancialService,
        private registrationService: RegistrationService,
        private userService: UserService,
    ) {
        super();
    }

    private static hoursByUser(totals: RegistrationTotal[], companies: Company[]): Map<number, HoursSplit> {
        const companyById = new Map(companies.map(company => [company.id, company]));
        const hours = new Map<number, HoursSplit>();
        for (const total of totals) {
            const company = companyById.get(total.company_id);
            const type: keyof HoursSplit = company?.is_internal ? 'internal' : company?.is_groundbirds ? 'groundbirds' : 'external';
            const split = hours.get(total.user_id) ?? {internal: 0, groundbirds: 0, external: 0};
            split[type] += Number(total.time) / 60;
            hours.set(total.user_id, split);
        }
        return hours;
    }

    /** Whole-number percentage, empty when it rounds to 0% */
    formatPercentage(percentage: number): string {
        const rounded = Math.round(percentage);
        return rounded ? `${rounded}%` : '';
    }

    hoursPercentage(split: HoursSplit | undefined, type: keyof HoursSplit): number {
        const total = split ? split.internal + split.groundbirds + split.external : 0;
        return total ? (split[type] / total) * 100 : 0;
    }

    hoursTotalPercentage(users: User[], hoursByUser: Map<number, HoursSplit>, type: keyof HoursSplit): number {
        const total = this.hoursTotal(users, hoursByUser, 'internal') + this.hoursTotal(users, hoursByUser, 'groundbirds') + this.hoursTotal(users, hoursByUser, 'external');
        return total ? (this.hoursTotal(users, hoursByUser, type) / total) * 100 : 0;
    }

    externalShare(users: User[], hoursByUser: Map<number, HoursSplit>, user: User): number {
        const total = this.hoursTotal(users, hoursByUser, 'external');
        return total ? ((hoursByUser.get(user.id)?.external ?? 0) / total) * 100 : 0;
    }

    hoursTotal(users: User[], hoursByUser: Map<number, HoursSplit>, type: keyof HoursSplit): number {
        return users.reduce((sum, user) => sum + (hoursByUser.get(user.id)?.[type] ?? 0), 0);
    }

    private static totalsByDebtor(invoices: AfasInvoice[]): Map<string, number> {
        const totals = new Map<string, number>();
        for (const invoice of invoices) {
            totals.set(invoice.debtorId, (totals.get(invoice.debtorId) ?? 0) + invoice.totaalExclBtw);
        }
        return totals;
    }

    get generalCosts(): number {
        if (!this.filteredUsers) return 0;
        const totalEmployeeCosts = Utils.sumProps(this.filteredUsers, 'period_costs');
        return totalEmployeeCosts * 0.1;
    }

    get daysElapsedInPeriod(): number {
        const today = new Date();

        // Get ISO week number (1-53)
        const getISOWeek = (date: Date): number => {
            const tempDate = new Date(date.getTime());
            tempDate.setHours(0, 0, 0, 0);
            tempDate.setDate(tempDate.getDate() + 3 - (tempDate.getDay() + 6) % 7);
            const week1 = new Date(tempDate.getFullYear(), 0, 4);
            return 1 + Math.round(((tempDate.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
        };

        const isoWeek = getISOWeek(today);

        // Calculate end of current period
        const endOfPeriod = new Date(today);
        const weeksToAdd = (4 - (isoWeek % 4)) % 4;
        endOfPeriod.setDate(endOfPeriod.getDate() + (weeksToAdd * 7));

        // Go to end of week (Sunday)
        const dayOfWeek = endOfPeriod.getDay();
        const daysToSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
        endOfPeriod.setDate(endOfPeriod.getDate() + daysToSunday);
        endOfPeriod.setHours(23, 59, 59, 999);

        // Start of period is 4 weeks before end + 1 day
        const startOfPeriod = new Date(endOfPeriod);
        startOfPeriod.setDate(startOfPeriod.getDate() - (4 * 7) + 1);
        startOfPeriod.setHours(0, 0, 0, 0);

        // Calculate working days elapsed (Monday to Friday only)
        let daysElapsed = 0;
        const currentDate = new Date(startOfPeriod);

        while (currentDate <= today) {
            const dayOfWeek = currentDate.getDay();
            // Count Monday(1) to Friday(5) as working days
            if (dayOfWeek >= 1 && dayOfWeek <= 5) {
                daysElapsed++;
            }
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return daysElapsed;
    }

    get filteredUsers(): User[] {
        if (!this.users()) return [];
        const today = new Date();
        const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
        return this.users().filter(user => {
            const start = user['employment_start'] ? new Date(user['employment_start']) : null;
            const end = user['employment_end'] ? new Date(user['employment_end']) : null;
            const startOk = !start || start <= today;
            const endOk = !end || end > threeMonthsAgo;
            return startOk && endOk;
        }).sort((a, b) => (b['period_costs'] ?? 0) - (a['period_costs'] ?? 0));
    }

    ngOnInit() {
        this.subscriptions.add(
            this.financialService.getHourlyEmployees().subscribe((employees) => {
                this.hourlyEmployees.set(employees);
                this.subscriptions.add(
                    this.userService.getList().subscribe((users) => {
                        this.users.set(users);
                        this.users().forEach(user => {
                            const emp = this.hourlyEmployees().find(e => e.id === user.id);
                            if (emp) {
                                user['prev_month'] = emp['prevmonth'];
                                user['cur_month'] = emp['curmonth'];
                            }
                        });
                        this.applyPCosts(this.users());
                    })
                );
            })
        );
        this.load();
        this.loadYearTotals();

        const isoPeriod = this.currentIsoPeriod();
        this.currentYear = isoPeriod.year;
        this.currentPeriod = isoPeriod.period;
        this.previousPeriod = isoPeriod.period === 1 ? 13 : isoPeriod.period - 1;
    }

    private applyPCosts(users: User[]): void {
        const pcosts: { [userId: number]: number } = JSON.parse(localStorage.getItem('pcosts') ?? '{}') ?? {};
        users.forEach(user => pcosts[user.id] ??= 123);
        localStorage.setItem('pcosts', JSON.stringify(pcosts));
        users.forEach(user => user['period_costs'] = pcosts[user.id] / 13);
    }

    private loadYearTotals(): void {
        this.subscriptions.add(this.companyService.getCompanies().subscribe(companies => this.allCompanies.set(companies)));
        const year = new Date().getFullYear();
        const groupBy = ['company_id', 'user_id'];
        this.subscriptions.add(
            combineLatest([
                this.registrationService.getTotals(year, null, null, null, groupBy),
                this.registrationService.getTotals(year - 1, null, null, null, groupBy),
            ]).subscribe(([current, previous]) => {
                this.currentYearTotals.set(current);
                this.previousYearTotals.set(previous);
            })
        );
    }

    load() {
        const companies$ = this.financialService.getHourlyCompanies();
        const invoices$ = this.afasService.getInvoices();

        this.subscriptions.add(
            combineLatest([companies$, invoices$]).subscribe(([companies, invoices]) => {
                this.hourlyCompanies.set(companies);
                this.invoices.set(invoices);
                if ((+(new Date()).getDate()) < 20) {
                    this.invoices()?.forEach(invoice => {
                        const invoiceMonth = +(new Date(invoice.nookPieceDate)).getMonth();
                        const currentMonth = +(new Date()).getMonth();
                        if (invoiceMonth == currentMonth) {
                            this.hourlyCompanies().forEach(company => {
                                // Support both Exact and AFAS during migration
                                if (company.afas_debtor_id == invoice.debtorId) {
                                    this.hasInvoice().push(company.id);
                                }
                            })
                        }
                    });
                }
            })
        );
    }

    invoiceMonth(companyId: number) {
        this.invoicing = companyId;
        this.subscriptions.add(
            this.afasService.invoiceMonth(companyId).subscribe(() => {
                this.hasInvoice().push(companyId);
                this.load();
                this.invoicing = null;
            }, () => {
                this.invoicing = null;
            })
        );
    }

    private currentIsoPeriod(): { year: number, period: number } {
        const today = new Date();
        const tmp = new Date(today);
        tmp.setDate(tmp.getDate() + 4 - (tmp.getDay() || 7));
        const year = tmp.getFullYear();
        const yearStart = new Date(year, 0, 4);
        yearStart.setDate(yearStart.getDate() - (yearStart.getDay() || 7) + 1);
        const daysSinceStart = Math.floor((today.getTime() - yearStart.getTime()) / 86400000);
        return {year, period: Math.min(Math.floor(daysSinceStart / 28) + 1, 13)};
    }
}
